import axios from 'axios'
import api from 'Api'
import { uuid } from 'vue-uuid'
import { SET_MESSAGE } from '../components/utils'
import { sortAlphabetically } from '../utils/main_helper'

const URL = '/cost-per-record-study'
const URL_BASICS_INDUSTRIES = '/basics/industries'
const URL_BASICS_BUSINESS_OPERATIONAL_REGIONS = '/basics/business-operational-regions'
const URL_BASICS_GLOBAL_REGULATIONS = '/basics/global-regulations'
const URL_BASICS_BASELINES = '/basics/baseline-cost-per-record-studies'

const state = {
  costPerRecordStudies: [],
  costPerRecordStudiesLoading: false,

  activeCostPerRecordStudy: {},

  industries: [],
  industriesLoading: false,
  businessOperationalRegions: [],
  businessOperationalRegionsLoading: false,
  globalRegulations: [],
  globalRegulationsLoading: false,
  baselines: [],
  baselinesLoading: false
}

const getters = {
  costPerRecordStudies: state => {
    return state.costPerRecordStudies
  },
  activeCostPerRecordStudy: state => {
    return state.activeCostPerRecordStudy
  },
  costPerRecordStudiesLoading: state => {
    return state.costPerRecordStudiesLoading
  },

  industries: state => {
    return sortAlphabetically(state.industries, 'industry')
  },
  industriesLoading: state => {
    return state.industriesLoading
  },
  businessOperationalRegions: state => {
    return sortAlphabetically(state.businessOperationalRegions, 'region')
  },
  businessOperationalRegionsLoading: state => {
    return state.businessOperationalRegionsLoading
  },
  globalRegulations: state => {
    return sortAlphabetically(state.globalRegulations, 'region')
  },
  globalRegulationsLoading: state => {
    return state.globalRegulationsLoading
  },
  baselines: state => {
    return state.baselines
  },
  baselinesLoading: state => {
    return state.baselinesLoading
  }
}

let loadCostPerRecordStudiesCancelToken = null
const actions = {
  async loadCostPerRecordStudies (context, organizationId) {
    if (!organizationId) {
      return
    }

    if (loadCostPerRecordStudiesCancelToken) {
      loadCostPerRecordStudiesCancelToken.cancel()
    }

    context.commit('SET_COST_PER_RECORD_STUDIES_LOADING', true)

    let response
    try {
      loadCostPerRecordStudiesCancelToken = axios.CancelToken.source()

      response = await api().get(`${URL}/organization/${organizationId}`, { cancelToken: loadCostPerRecordStudiesCancelToken.token })
      context.commit('SET_COST_PER_RECORD_STUDIES', response.data)

      loadCostPerRecordStudiesCancelToken = null
    } catch (e) {
      if (e && e.constructor && e.constructor.name === 'Cancel') {
      } else {
        SET_MESSAGE({ type: 'error', message: (response && response.error) || 'Error' })
      }
    }

    context.commit('SET_COST_PER_RECORD_STUDIES_LOADING', false)
  },

  async saveCostPerRecordStudy (context, payload) {
    let isAddingNew = false
    const oldOnes = state.costPerRecordStudies
    if (!payload.entity_id) {
      payload.entity_id = uuid.v4()
      isAddingNew = true

      context.commit('SET_COST_PER_RECORD_STUDIES', [...state.costPerRecordStudies, payload])
    } else {
      context.commit('SET_COST_PER_RECORD_STUDIES', [...state.costPerRecordStudies.map(item => item.entity_id !== payload.entity_id ? item : payload)])
    }

    let response
    try {
      if (isAddingNew) {
        response = await api().post(URL, payload)
      } else {
        response = await api().put(`${URL}`, payload)
      }
    } catch (e) {
      SET_MESSAGE({ type: 'error', message: (response && response.error) || 'Error' })

      context.commit('SET_COST_PER_RECORD_STUDIES', oldOnes)

      return
    }

    context.commit('SET_COST_PER_RECORD_STUDIES', [...state.costPerRecordStudies.map(item => item.entity_id !== payload.entity_id ? item : response.data)])

    SET_MESSAGE({ type: 'success', message: 'A cost per record study has been saved successfully.' })
  },

  async deleteCostPerRecordStudy (context, id) {
    const oldOnes = state.costPerRecordStudies

    context.commit('SET_COST_PER_RECORD_STUDIES', [...state.costPerRecordStudies.filter(item => item.entity_id !== id)])

    let response
    try {
      response = await api().delete(`${URL}/${id}`)
    } catch (e) {
      SET_MESSAGE({ type: 'error', message: (response && response.error) || 'Error' })

      context.commit('SET_COST_PER_RECORD_STUDIES', oldOnes)
    }

    SET_MESSAGE({ type: 'success', message: 'A cost per record study has been deleted successfully.' })
  },

  resetActiveCostPerRecordStudy (context) {
    const usedIndustries = context.getters.costPerRecordStudies.map(({ industry }) => industry)
    const industries = context.getters.cyberSecurityAssessments.map(({ industry }) => industry).filter(industry => usedIndustries.indexOf(industry) < 0)

    context.commit('SET_ACTIVE_COST_PER_RECORD_STUDY', {
      industry: industries && industries.length ? industries[0] : null,
      global_regions: null,
      customer_regions: null,
      revenue: null,

      unadj_cost_per_record_min: 0,
      unadj_cost_per_record_max: 0,

      discount_factor: 100,

      adj_cost_per_record_min: 0,
      adj_cost_per_record_max: 0
    })
  },

  async loadActiveCostPerRecordStudy (context, id) {
    context.commit('SET_ACTIVE_COST_PER_RECORD_STUDY', state.costPerRecordStudies.find(item => item.entity_id === id))

    let response
    try {
      response = await api().get(`${URL}/${id}`)
      context.commit('SET_ACTIVE_COST_PER_RECORD_STUDY', response.data)
    } catch (e) {
      SET_MESSAGE({ type: 'error', message: (response && response.error) || 'Error' })
    }
  },

  async loadIndustries (context) {
    if (state.industriesLoading) {
      return
    }

    context.commit('SET_INDUSTRIES_LOADING', true)

    let response
    try {
      response = await api().get(`${URL_BASICS_INDUSTRIES}`)
      context.commit('SET_INDUSTRIES', response.data)
    } catch (e) {
      SET_MESSAGE({ type: 'error', message: (response && response.error) || 'Error' })
    }

    context.commit('SET_INDUSTRIES_LOADING', false)
  },

  async loadBusinessOperationalRegions (context) {
    if (state.businessOperationalRegionsLoading) {
      return
    }

    context.commit('SET_BUSINESS_OPERATIONAL_REGIONS_LOADING', true)

    let response
    try {
      response = await api().get(`${URL_BASICS_BUSINESS_OPERATIONAL_REGIONS}`)
    } catch (e) {
      SET_MESSAGE({ type: 'error', message: (response && response.error) || 'Error' })
      return
    }
    context.commit('SET_BUSINESS_OPERATIONAL_REGIONS', response.data)

    context.commit('SET_BUSINESS_OPERATIONAL_REGIONS_LOADING', false)
  },

  async loadGlobalRegulations (context) {
    if (state.globalRegulationsLoading) {
      return
    }

    context.commit('SET_GLOBAL_REGULATIONS_LOADING', true)

    let response
    try {
      response = await api().get(`${URL_BASICS_GLOBAL_REGULATIONS}`)
    } catch (e) {
      SET_MESSAGE({ type: 'error', message: (response && response.error) || 'Error' })
    }
    context.commit('SET_GLOBAL_REGULATIONS', response.data)

    context.commit('SET_GLOBAL_REGULATIONS_LOADING', false)
  },

  async loadBaselines (context) {
    if (state.baselinesLoading) {
      return
    }

    context.commit('SET_BASELINES_LOADING', true)

    let response
    try {
      response = await api().get(`${URL_BASICS_BASELINES}`)
    } catch (e) {
      SET_MESSAGE({ type: 'error', message: (response && response.error) || 'Error' })
      return
    }
    context.commit('SET_BASELINES', response.data)

    context.commit('SET_BASELINES_LOADING', false)
  }
}

const mutations = {
  SET_COST_PER_RECORD_STUDIES (state, costPerRecordStudies) {
    state.costPerRecordStudies = costPerRecordStudies
  },
  SET_COST_PER_RECORD_STUDIES_LOADING (state, loading) {
    state.costPerRecordStudiesLoading = loading
  },
  SET_ACTIVE_COST_PER_RECORD_STUDY (state, costPerRecordStudy) {
    state.activeCostPerRecordStudy = costPerRecordStudy
  },

  SET_INDUSTRIES (state, industries) {
    state.industries = industries
  },
  SET_INDUSTRIES_LOADING (state, loading) {
    state.industriesLoading = loading
  },
  SET_BUSINESS_OPERATIONAL_REGIONS (state, regions) {
    state.businessOperationalRegions = regions
  },
  SET_BUSINESS_OPERATIONAL_REGIONS_LOADING (state, loading) {
    state.businessOperationalRegionsLoading = loading
  },
  SET_GLOBAL_REGULATIONS (state, regions) {
    state.globalRegulations = regions
  },
  SET_GLOBAL_REGULATIONS_LOADING (state, loading) {
    state.globalRegulationsLoading = loading
  },
  SET_BASELINES (state, baselines) {
    state.baselines = baselines
  },
  SET_BASELINES_LOADING (state, loading) {
    state.baselinesLoading = loading
  }
}

export default {
  state,
  getters,
  actions,
  mutations,
  name: 'costPerRecordStudyStore'
}
